import React from "react";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { Button, Stack } from "@mui/material";
import { toast } from "react-toastify";
import { useSpecsPageContext } from "../SpecsPageContext";
import { SpecsFabricsModel } from "../specsModel/SpecsFabricsModel";
import { exportFabricToCsv, importFabricFromCsv } from "./fabricsCsv";

function getClipboard() {
  if (!navigator.clipboard) {
    throw new Error("Clipboard API is not available in this browser");
  }

  return navigator.clipboard;
}

export default function FabricsCsvActions() {
  const { selectedValue, draftModel, onReplaceDraftFromSnapshot } =
    useSpecsPageContext();
  const [loadingAction, setLoadingAction] = React.useState<
    "import" | "export" | null
  >(null);

  const disabled = !selectedValue || !(draftModel instanceof SpecsFabricsModel);

  const handleExport = React.useCallback(async () => {
    if (!(draftModel instanceof SpecsFabricsModel)) {
      return;
    }

    setLoadingAction("export");
    try {
      const snapshot = draftModel.save();
      await getClipboard().writeText(exportFabricToCsv(snapshot));
      toast.success(`Copied fabric CSV: ${snapshot.name || selectedValue}`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to copy fabric CSV"
      );
    } finally {
      setLoadingAction(null);
    }
  }, [draftModel, selectedValue]);

  const handleImport = React.useCallback(async () => {
    setLoadingAction("import");
    try {
      const csvText = await getClipboard().readText();
      const snapshot = importFabricFromCsv(csvText);
      onReplaceDraftFromSnapshot(snapshot);
      toast.success(`Imported fabric CSV: ${snapshot.name}`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to import fabric CSV"
      );
    } finally {
      setLoadingAction(null);
    }
  }, [onReplaceDraftFromSnapshot]);

  return (
    <Stack direction="row" justifyContent="flex-end" spacing={1}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<UploadFileOutlinedIcon />}
        disabled={disabled || loadingAction !== null}
        onClick={handleImport}
      >
        {loadingAction === "import" ? "Importing..." : "Import CSV"}
      </Button>
      <Button
        variant="outlined"
        size="small"
        startIcon={<DownloadOutlinedIcon />}
        disabled={disabled || loadingAction !== null}
        onClick={handleExport}
      >
        {loadingAction === "export" ? "Exporting..." : "Export CSV"}
      </Button>
    </Stack>
  );
}
